<template>
  <div class="home">
    <!-- Hero: Value Prop + Phone Showcase -->
    <section class="hero-split">
      <div class="container hero-grid">
        <div class="hero-copy">
          <h1>Bet with the internet on your side.</h1>
          <h2>The sports betting analytics platform built for thinkers.</h2>
          <p>Everybody bets, almost nobody wins. Paulie connects media, sportsbooks, and prediction markets to monitor the relationship between sentiment and money flows.</p>
          <ul class="hero-bullets">
            <li>Compare sportsbook, prediction-market, and social signals in one place.</li>
            <li>View the value of wagers on each side of a market at books and prediction markets.</li>
            <li>Get in-app alerts for market opportunities and data abnormalities.</li>
            <li>Follow ROI trends from every angle of the market.</li>
            <li>Chat directly with the database for any idea you have.</li>
          </ul>
        </div>
        <div class="hero-phones">
          <img src="/white-home-asset.png" alt="Paulie market comparison dashboard and mobile app" class="home-hero-asset" />
          <img src="/alert-trend-bubble-row.svg" alt="Paulie Joes vs Pros alert hit-rate preview" class="hero-alert-asset" />
          <div class="hero-actions hero-actions--asset">
            <router-link to="/data" class="btn hero-cta hero-cta-secondary">Explore Data</router-link>
            <button class="btn hero-cta hero-cta-primary" @click="scrollToDownload">Download App</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Chat with the Database — full width -->
    <section class="chat-section">
      <div class="container">
        <div class="chat-header">
          <h2>Millions of Data Points at Your Service</h2>
          <p>Paulie is scraping data 24/7 to provide the data-based narrative the bookmakers don't want you to know.</p>
        </div>
        <div class="chat-features">
          <div class="chat-feature">
            <div class="chat-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            </div>
            <h3>Real-Time Odds</h3>
            <p>Conversational access to live odds and lines across sportsbooks and prediction markets.</p>
          </div>
          <div class="chat-feature">
            <div class="chat-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <h3>Historical Results</h3>
            <p>Trend and pattern analysis powered by game results going back years.</p>
          </div>
          <div class="chat-feature">
            <div class="chat-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
            </div>
            <h3>Sportsbook / Prediction Market Mapped</h3>
            <p>Drawing insight about oddsmaker and public sentiment from line movement on sportsbooks and prediction markets.</p>
          </div>
          <div class="chat-feature">
            <div class="chat-feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </div>
            <h3>Sentiment Signals</h3>
            <p>Aggregated from podcasts, social media, news, and the broader sports betting community.</p>
          </div>
        </div>

      </div>
    </section>

    <!-- Influencer Section -->
    <section v-if="influencerEnabled" class="influencers">
      <div class="container">
        <div class="influencers-content">
          <h2>Want to Earn with the Family?</h2>
          <p>Join our unprecendented influencer revenue sharing program. If approved, each user who creates a subscription and has signed up with your referral code you are subject to up to 20% of the subscription revenue each month.</p>
          <div class="influencer-benefits">
            <div class="benefit-item">
              <div class="benefit-icon">📈</div>
              <span>Monthly Payouts</span>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">💰</div>
              <span>Monetize Content</span>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">🤝</div>
              <span>Exclusive Opportunities</span>
            </div>
          </div>
          <button class="btn btn-primary btn-large" @click="$router.push('/influencer-apply')">Apply Now</button>
        </div>
      </div>
    </section>

    <!-- Download Section -->
    <section id="download" class="download">
      <div class="container">
        <div class="download-content">
          <h2>Download Now</h2>
          <p>Start collecting insights from Paulie. Available on iOS and Android.</p>
          <div class="download-buttons">
            <a :href="IOS_APP_STORE_URL" target="_blank" rel="noopener noreferrer" class="download-btn">
              <img src="/apple-store-badge.png" alt="Download on the App Store" />
            </a>
            <button class="download-btn" @click="showComingSoon = true">
              <img src="/playstore-badge.png" alt="Get it on Google Play" />
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Coming Soon Modal -->
    <Teleport to="body">
      <div v-if="showComingSoon" class="modal-overlay" @click="showComingSoon = false">
        <div class="modal-card" @click.stop>
          <h3>Coming Soon</h3>
          <p>The Paulie mobile app is currently in development. Stay tuned!</p>
          <button class="btn btn-secondary" @click="showComingSoon = false">Got it</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { IOS_APP_STORE_URL } from '@/lib/appLinks'

const showComingSoon = ref(false)
const influencerEnabled = import.meta.env.VITE_FEATURE_INFLUENCER === 'true'

const scrollToDownload = () => {
  const downloadSection = document.querySelector('.download')
  if (downloadSection) {
    downloadSection.scrollIntoView({ behavior: 'smooth' })
  }
}
</script>

<style scoped>
.home {
  min-height: 100vh;
  background: transparent;
  position: relative;
}

/* ── Hero Split: Copy left + Phones right ── */
.hero-split {
  padding: 2rem 0 2.35rem;
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 2rem;
  align-items: start;
}

.hero-copy h1 {
  font-size: clamp(2.25rem, 4.5vw, 3.35rem);
  margin: 0 0 0.6rem;
  letter-spacing: -0.04em;
  line-height: 1.02;
  color: var(--text-primary);
}

.hero-copy h2 {
  font-size: clamp(1.1rem, 2.2vw, 1.35rem);
  color: var(--accent);
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.hero-copy > p {
  color: var(--text-secondary);
  line-height: 1.55;
  margin-bottom: 0.9rem;
}

.hero-bullets {
  list-style: none;
  margin: 0;
}

.hero-bullets li {
  padding: 0.28rem 0 0.28rem 1.35rem;
  color: var(--text-secondary);
  position: relative;
  line-height: 1.35;
}

.hero-bullets li::before {
  content: '✦';
  position: absolute;
  left: 0;
  color: var(--accent);
  font-weight: 700;
}

.hero-actions {
  display: flex;
  gap: 0.85rem;
  align-items: center;
  flex-wrap: wrap;
}

.hero-cta {
  min-height: 52px;
  padding: 0.9rem 1.35rem;
  border-radius: 12px;
  font-size: 0.98rem;
  font-weight: 800;
  letter-spacing: 0;
}

.hero-cta-primary {
  border: 0;
  background: var(--accent);
  color: #fff;
  box-shadow: 0 14px 34px rgba(91, 124, 255, 0.3);
}

.hero-cta-primary:hover {
  background: #3654ff;
  box-shadow: 0 18px 44px rgba(91, 124, 255, 0.36);
}

.hero-cta-secondary {
  background: #080808;
  border-color: #080808;
  color: #fff;
  box-shadow: 0 12px 28px rgba(8, 8, 8, 0.16);
}

.hero-cta-secondary:hover {
  background: #202020;
  border-color: #202020;
  box-shadow: 0 16px 36px rgba(8, 8, 8, 0.2);
}

.hero-phones {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 0;
  padding-top: 1.2rem;
}

.home-hero-asset {
  display: block;
  width: min(730px, 112%);
  height: auto;
  max-width: none;
  transform: translateX(2%);
}

.hero-alert-asset {
  display: block;
  width: min(760px, 112%);
  max-width: none;
  height: auto;
  margin-top: 0.55rem;
  transform: translateX(2%);
}

.hero-actions--asset {
  justify-content: center;
  margin-top: 0.75rem;
}

.hero-tagline {
  font-style: italic;
  font-weight: 600;
  font-size: clamp(0.95rem, 1.3vw, 1.1rem);
  color: var(--text-muted);
  text-align: center;
  padding: 0.75rem 2rem 0;
  line-height: 1.6;
  max-width: 660px;
  margin: 0 auto;
  text-decoration: underline;
  text-decoration-color: rgba(167, 139, 250, 0.44);
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
}

/* ── Chat Section: full width, horizontal features ── */
.chat-section {
  padding: 3rem 0;
  border-top: 1px solid #eeeeea;
}

.chat-header {
  text-align: center;
  max-width: 760px;
  margin: 0 auto 2.5rem;
}

.chat-header h2 {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  margin-bottom: 0.8rem;
  color: var(--text-primary);
}

.chat-header p {
  color: var(--text-secondary);
  line-height: 1.7;
}

.chat-features {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;
}

.chat-feature {
  padding: 1.4rem;
  border-radius: 10px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.chat-feature-icon {
  color: var(--accent);
  margin-bottom: 0.75rem;
}

.chat-feature h3 {
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
  color: var(--text-primary);
}

.chat-feature p {
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.55;
}


/* Responsive */
@media (max-width: 768px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }

  .hero-bullets li {
    text-align: left;
  }

  .hero-actions {
    justify-content: center;
  }

  .home-hero-asset {
    width: min(100%, 680px);
    max-width: 100%;
    transform: none;
  }

  .hero-alert-asset {
    width: min(100%, 680px);
    max-width: 100%;
    transform: none;
  }

  .hero-phones {
    padding-top: 0;
  }

  .chat-features {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .chat-features {
    grid-template-columns: 1fr;
  }
}

.home::before,
.home::after {
  display: none;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}



.influencers {
  padding: 4rem 0;
}

.influencers-content {
  text-align: center;
  max-width: 860px;
  margin: 0 auto;
  padding: 2.4rem;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
}

.influencers-content::before,
.influencers-content::after {
  display: none;
}

.influencers-content h2 {
  font-size: clamp(1.9rem, 3.5vw, 2.6rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  text-shadow: none;
}

.influencers-content p {
  font-size: 1.05rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 2rem;
}

.influencer-benefits {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #fbfbf8;
  border-radius: 10px;
  border: 1px solid var(--border);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.25s ease, box-shadow 0.25s ease;
  box-shadow: none;
}

.benefit-item:hover {
  transform: translateY(-3px);
  border-color: rgba(91, 124, 255, 0.36);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.25);
}

.benefit-icon {
  font-size: 1.9rem;
}

.benefit-item span {
  font-weight: 600;
  color: var(--text-primary);
}

.influencers .btn-primary {
  position: relative;
  border-color: transparent;
  color: #ffffff;
  background: var(--accent);
  box-shadow: var(--shadow-glow);
}

.influencers .btn-primary:hover {
  filter: brightness(1.04);
}

.download {
  padding: 4rem 0 5rem;
}

.download-content {
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
  padding: 2rem;
  border-radius: 10px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}

.download-content h2 {
  font-size: clamp(1.9rem, 3.5vw, 2.6rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.download-content p {
  font-size: 1.05rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 2rem;
}

.download-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.download-btn {
  display: inline-flex;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), filter 0.25s ease;
}

.download-btn:hover {
  transform: translateY(-3px);
  filter: brightness(1.06);
}

.download-btn img {
  height: 50px;
  width: auto;
}

.download-btn:first-child img {
  border: 1px solid var(--border);
  border-radius: 8px;
  height: 70px;
  transform: translateY(-12px);
}

.download-btn:last-child img {
  height: 70px;
  transform: translateY(-12px);
}

@keyframes influencer-shimmer {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }

  .influencers-content,
  .download-content {
    padding: 1.4rem;
    border-radius: 16px;
  }

  .influencer-benefits {
    grid-template-columns: repeat(2, 1fr);
  }

  .download-buttons {
    flex-direction: column;
    align-items: center;
  }
}
</style>

<style>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.78);
}

.modal-card {
  text-align: center;
  max-width: 400px;
  width: 90%;
  padding: 2.2rem;
  border-radius: 10px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}

.modal-card h3 {
  font-size: 1.6rem;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
}

.modal-card p {
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}
</style>
